import type * as React from 'react';
import type { DraftWriteResult, FrameworkContent } from '@/lib/api/discovery-frameworks';

/**
 * What the tabs and side panels of the Discovery frameworks screen share
 * (T-0184): the content on screen, whether it is the editable draft, and
 * one way to write to the draft.
 */

export interface WriteOptions {
  /** What is being saved, for the error line: "D5", "Q-B.3". */
  what: string;
  /** Show a refusal in place (an add form) instead of the screen's error line. */
  onError?: (err: unknown) => void;
}

/**
 * One draft write. The screen supplies the framework id and the
 * `base_revision`, runs writes one at a time, and deals with a stale draft
 * itself (rule 2). Resolves to null when the write did not happen.
 */
export type DraftWrite = (
  fn: (frameworkId: string, base: number) => Promise<DraftWriteResult>,
  options: WriteOptions,
) => Promise<DraftWriteResult | null>;

export interface EditorCtx {
  content: FrameworkContent;
  /** The draft is loaded and editable. */
  editing: boolean;
  /** A draft write is in flight. */
  busy: boolean;
  write: DraftWrite;
}

const NOT_ADDED = 'Not added: the draft was reloaded. Check it, then add again.';

/** An add, with its refusal in words for the add form (or null when it was added). */
export async function tryAdd(
  write: DraftWrite,
  fn: (frameworkId: string, base: number) => Promise<DraftWriteResult>,
  what: string,
  describe: (err: unknown) => React.ReactNode,
): Promise<{ result: DraftWriteResult | null; refusal: React.ReactNode | null }> {
  const box: { refusal: React.ReactNode | null } = { refusal: null };
  const result = await write(fn, {
    what,
    onError: (err) => {
      box.refusal = describe(err);
    },
  });
  return result ? { result, refusal: null } : { result: null, refusal: box.refusal ?? NOT_ADDED };
}
